import { Button, FileInput, Label, Radio, Spinner, TextInput } from 'flowbite-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { approveAction, moveToNextRound, rejectAction, rejectCandidate, submitAssignment } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { Drawer } from 'src/components/shared/Drawer';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import {
	ApplicationRoundStatus,
	ApplicationRoundType,
	AssignmentSubmissionType,
	UIApplication,
	UIExtendedApplication,
	UIRemarksDrawerType,
	UIUser,
	UserRole,
} from 'types';
import { handleS3Upload } from 'src/utils/imageUpload';
import RemarksDrawer from '../application-card-v2/RemarksDrawer';
import { Img } from 'react-image';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import RecruiterCandidateMoveToNextRound from 'src/components/recruiterPositions/RecruiterCandidateMoveToNextRound';
import { useMediaQuery } from '@mui/material';
import { HiClock } from 'react-icons/hi';
interface Props {
	application: UIApplication | UIExtendedApplication;
	positionId?: string;
	jobseekerHandler?: string;
	applicationUser?: UIUser;
}

const ViewAssignment = ({ application, jobseekerHandler, positionId, applicationUser }: Props) => {
	const { user, isAccountManagerLoggedIn } = useUserContext();
	const { refetch: refetchPositionById } = useGetPositionByIdQuery(positionId);
	const ongoingRound = application?.ongoingRound;
	const isMobile = useMediaQuery('(max-width:960px)');
	const lastSubmission = ongoingRound?.assignmentSubmissions?.[ongoingRound?.assignmentSubmissions?.length - 1];
	const isOnlineTest = ongoingRound?.type === ApplicationRoundType.OnlineTest;
	const assignmentSubmissionType = ongoingRound?.assignmentSubmissionType ?? AssignmentSubmissionType.Link;
	const canSubmitLink =
		assignmentSubmissionType === AssignmentSubmissionType.Link ||
		assignmentSubmissionType === AssignmentSubmissionType.Both ||
		assignmentSubmissionType === AssignmentSubmissionType.Other;
	const canSubmitFile =
		assignmentSubmissionType === AssignmentSubmissionType.File ||
		assignmentSubmissionType === AssignmentSubmissionType.Both ||
		assignmentSubmissionType === AssignmentSubmissionType.Other;
	const isOtherSubmission = assignmentSubmissionType === AssignmentSubmissionType.Other;

	const [submissionLink, setSubmissionLink] = useState<string>(
		lastSubmission?.submissionLink || lastSubmission?.submissionLink || '',
	);
	const [submissionFile, setSubmissionFile] = useState<File | null>(null);
	const [isUploadingSubmission, setIsUploadingSubmission] = useState<boolean>(false);
	const [submissionMode, setSubmissionMode] = useState<AssignmentSubmissionType.Link | AssignmentSubmissionType.File>(
		canSubmitLink ? AssignmentSubmissionType.Link : AssignmentSubmissionType.File,
	);

	// New: structured submissions by type
	const [linkSubmissions, setLinkSubmissions] = useState<Record<string, string>>({});
	const [fileSubmissions, setFileSubmissions] = useState<Record<string, File>>({});
	const [filesToReplace, setFilesToReplace] = useState<Set<string>>(new Set()); // Track files being replaced
	const [showStructuredSubmitUi, setShowStructuredSubmitUi] = useState<boolean>(false);
	const [acknowledgmentChecked, setAcknowledgmentChecked] = useState<boolean>(false); // For "Other" type acknowledgment
	const [noSubmissionAcknowledgmentChecked, setNoSubmissionAcknowledgmentChecked] = useState<boolean>(false); // For "No Submission" type acknowledgment

	// Pre-populate existing submissions
	useEffect(() => {
		if (lastSubmission?.submittedLinks && lastSubmission.submittedLinks.length > 0) {
			const linkMap: Record<string, string> = {};
			lastSubmission.submittedLinks.forEach(link => {
				linkMap[link.type] = link.url;
			});
			setLinkSubmissions(linkMap);
		}
	}, [lastSubmission?.submittedLinks]);

	// Check if there are existing structured submissions
	const hasExistingStructuredSubmission =
		(lastSubmission?.submittedLinks && lastSubmission.submittedLinks.length > 0) ||
		(lastSubmission?.submittedFiles && lastSubmission.submittedFiles.length > 0);

	useEffect(() => {
		setSubmissionLink(lastSubmission?.submissionLink || lastSubmission?.submissionLink || '');
	}, [lastSubmission?.submissionLink]);

	useEffect(() => {
		setSubmissionMode(canSubmitLink ? AssignmentSubmissionType.Link : AssignmentSubmissionType.File);
		setSubmissionFile(null);
	}, [assignmentSubmissionType, canSubmitLink, canSubmitFile]);

	const isDocumentLink = (link?: string) => {
		if (!link) return false;
		const cleanLink = link.split('?')[0] || '';
		return /\.(pdf|doc|docx)$/i.test(cleanLink);
	};
	const fallbackAssignmentLink = ongoingRound?.interviewOrAssignmentLink;
	const assignmentDocumentLink =
		ongoingRound?.assignmentDocumentLink || (isDocumentLink(fallbackAssignmentLink) ? fallbackAssignmentLink : undefined);
	const assignmentExternalLink =
		ongoingRound?.assignmentLink || (!assignmentDocumentLink ? fallbackAssignmentLink : undefined);
	const assignmentLinkOptions = ongoingRound?.assignmentLinkOptions || [];
	const assignmentFileOptions = ongoingRound?.assignmentFileOptions || [];
	const hasLinkOptions = assignmentLinkOptions.length > 0;
	const hasFileOptions = assignmentFileOptions.length > 0;
	const isNoSubmission = assignmentLinkOptions.includes('noSubmission');
	const linkOptionLabelMap: Record<string, string> = {
		github: 'GitHub link',
		deployment: 'Deployment link',
		drive: 'Drive link',
		googleCollab: 'Google Collab link',
		submission: 'Submission link',
		noSubmission: 'No Submission',
		other: 'Other link',
	};
	const fileOptionLabelMap: Record<string, string> = {
		pdf: 'PDF',
		doc: 'DOC',
		docx: 'DOCX',
		zip: 'ZIP',
		excel: 'Excel',
		other: 'Other file',
	};
	const fileAcceptMap: Record<string, string> = {
		pdf: '.pdf',
		doc: '.doc',
		docx: '.docx',
		zip: '.zip',
		excel: '.xls,.xlsx',
	};
	const fileAcceptAttr = assignmentFileOptions.includes('other')
		? undefined
		: assignmentFileOptions.length === 0
			? '.pdf,.doc,.docx,.zip'
			: assignmentFileOptions
				.map(option => fileAcceptMap[option] || '')
				.filter(Boolean)
				.join(',');

	const isDeadlinePassed = ongoingRound?.expiry ? moment(ongoingRound?.expiry).isBefore(moment()) : '';
	const jobseekerExpiryDate = ongoingRound?.jobSeekerAssignmentExpiry || ongoingRound?.expiry;
	const isJobSeekerDeadlinePassed = jobseekerExpiryDate ? moment(jobseekerExpiryDate).isBefore(moment()) : '';
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isJobseekerLoggedIn } = useUserContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const submitAssignmentMutation = useMutation(['submitAssignment'], submitAssignment, {
		onSuccess() {
			toast({
				title: 'Assignment submitted successfully!',
				type: 'success',
			});
			setSubmissionFile(null);
			setShowSubmitUi(false);
			setShowStructuredSubmitUi(false); // Hide structured form after successful submission
			setAcknowledgmentChecked(false); // Reset acknowledgment checkbox
			setNoSubmissionAcknowledgmentChecked(false); // Reset no submission acknowledgment checkbox
			queryClient.refetchQueries(['getAllApplications']);
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('viewAssignment');
			setSearchParams(newSearchParams);
			navigate(`/my-applications?${newSearchParams.toString()}`);
		},
		onError(error: Error) {
			toast({
				title: `Error while submitting assignment: ${error.message}`,
				type: 'error',
			});
		},
	});
	const shouldDisableSubmission = () => {
		if (!ongoingRound?.status) return;
		// Only disable submission for non-Masai students when deadline has passed
		if (!user?.flags?.isMasaiStudent && isJobSeekerDeadlinePassed) {
			return true;
		}
	};
	const handleAssignmentSubmission = async () => {
		// Special handling for "Other" type with external link (acknowledgment only)
		if (isOtherSubmission && assignmentExternalLink) {
			if (!acknowledgmentChecked) {
				toast({
					title: 'Please acknowledge that you have filled out the submission form',
					type: 'error',
				});
				return;
			}
			try {
				setIsUploadingSubmission(true);
				// Submit the external link as an "other" type link to indicate acknowledgment
				submitAssignmentMutation.mutate({
					applicationId: application._id || '',
					submissionLink: assignmentExternalLink,
					submittedLinks: [{ type: 'other', url: assignmentExternalLink }],
					userId: application.user?._id,
					submissionType: AssignmentSubmissionType.Other,
				});
				return;
			} catch (error: any) {
				toast({
					title: error?.message || 'Unable to submit assignment',
					type: 'error',
				});
				return;
			} finally {
				setIsUploadingSubmission(false);
			}
		}

		// Validation: check if required links are provided (exclude noSubmission as it's handled separately)
		if (hasLinkOptions) {
			const requiredOptions = assignmentLinkOptions.filter(opt => opt !== 'noSubmission');
			const missingLinks = requiredOptions.filter(opt => !linkSubmissions[opt]?.trim());
			if (missingLinks.length > 0) {
				toast({
					title: `Please provide all required links: ${missingLinks.map(l => linkOptionLabelMap[l] || l).join(', ')}`,
					type: 'error',
				});
				return;
			}
			// Validate URL format for each link (exclude noSubmission)
			for (const opt of requiredOptions) {
				const url = linkSubmissions[opt]?.trim();
				if (url) {
					const domainUrlPattern = new RegExp(
						'^(https?:\\/\\/)?' +
						'(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}',
						'i',
					);
					if (!domainUrlPattern.test(url)) {
						toast({
							title: `Invalid URL for ${linkOptionLabelMap[opt]}: ${url}`,
							type: 'error',
						});
						return;
					}
				}
			}
		}

		// Validation: check if required files are selected (new file OR existing file)
		// If a file is marked for replacement, it must have a new file uploaded
		if (hasFileOptions) {
			const missingFiles = assignmentFileOptions.filter(opt => {
				const hasNewFile = !!fileSubmissions[opt];
				const isReplacing = filesToReplace.has(opt);
				const hasExistingFile = lastSubmission?.submittedFiles?.some(f => f.type === opt);
				// If replacing, must have new file. Otherwise, can use existing or new file.
				return isReplacing ? !hasNewFile : !hasNewFile && !hasExistingFile;
			});
			if (missingFiles.length > 0) {
				toast({
					title: `Please upload all required files: ${missingFiles.map(f => fileOptionLabelMap[f] || f).join(', ')}`,
					type: 'error',
				});
				return;
			}
		}

		// Legacy validation (only if not using new structured format)
		if (!hasLinkOptions && !hasFileOptions) {
			const requiresFile = submissionMode === AssignmentSubmissionType.File;
			if (requiresFile && !submissionFile) {
				toast({
					title: 'Please select a file to upload',
					type: 'error',
				});
				return;
			}
			const trimmedLink = submissionLink?.trim();
			if (!requiresFile) {
				const domainUrlPattern = new RegExp(
					'^(https?:\\/\\/)?' +
					'(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}',
					'i',
				);
				if (!trimmedLink || !domainUrlPattern.test(trimmedLink)) {
					toast({
						title: 'Invalid URL',
						type: 'error',
					});
					return;
				}
			}
		}

		try {
			setIsUploadingSubmission(true);
			const trimmedLink = submissionLink?.trim();
			const requiresFile = submissionMode === AssignmentSubmissionType.File;
			let submissionPayloadLink = trimmedLink || '';

			// Upload single file (legacy mode)
			if (!hasLinkOptions && !hasFileOptions && requiresFile && submissionFile) {
				const fileSizeMB = submissionFile.size / (1024 * 1024);
				if (fileSizeMB > 5) {
					toast({
						title: 'File size exceeds the maximum limit of 5 MB',
						type: 'error',
					});
					return;
				}
				const policy = await getPolicyForS3NonVideo(submissionFile.name, 'jobseekerAssignments', submissionFile.type, true);
				const uploadedUrl = await handleS3Upload(policy, submissionFile);
				if (!uploadedUrl) {
					throw new Error('File upload failed');
				}
				submissionPayloadLink = uploadedUrl;
			}

			// Build structured submissions for new mode
			const submittedLinks: Array<{ type: string; url: string }> = [];
			if (hasLinkOptions) {
				assignmentLinkOptions.forEach(opt => {
					const url = linkSubmissions[opt]?.trim();
					if (url) {
						submittedLinks.push({ type: opt, url });
					}
				});
			}

			const submittedFiles: Array<{ type: string; fileUrl: string; fileName: string; fileSize?: number }> = [];
			if (hasFileOptions) {
				for (const opt of assignmentFileOptions) {
					const newFile = fileSubmissions[opt];
					const existingFile = lastSubmission?.submittedFiles?.find(f => f.type === opt);

					if (newFile) {
						// New file uploaded - upload to S3
						const fileSizeMB = newFile.size / (1024 * 1024);
						if (fileSizeMB > 5) {
							toast({
								title: `File size for ${fileOptionLabelMap[opt]} exceeds 5 MB`,
								type: 'error',
							});
							return;
						}
						const policy = await getPolicyForS3NonVideo(newFile.name, 'jobseekerAssignments', newFile.type, true);
						const uploadedUrl = await handleS3Upload(policy, newFile);
						if (!uploadedUrl) {
							throw new Error(`Upload failed for ${fileOptionLabelMap[opt]}`);
						}
						submittedFiles.push({
							type: opt,
							fileUrl: uploadedUrl,
							fileName: newFile.name,
							fileSize: newFile.size,
						});
					} else if (existingFile) {
						// No new file uploaded, keep existing file
						submittedFiles.push({
							type: existingFile.type,
							fileUrl: existingFile.fileUrl,
							fileName: existingFile.fileName,
							fileSize: existingFile.fileSize,
						});
					}
				}
			}

			// Determine actual submission type based on what was submitted
			let actualSubmissionType: AssignmentSubmissionType;
			if (hasLinkOptions || hasFileOptions) {
				// New structured format
				const hasLinks = submittedLinks.length > 0;
				const hasFiles = submittedFiles.length > 0;

				if (hasLinks && hasFiles) {
					actualSubmissionType = AssignmentSubmissionType.Both;
				} else if (hasFiles) {
					actualSubmissionType = AssignmentSubmissionType.File;
				} else if (hasLinks) {
					actualSubmissionType = AssignmentSubmissionType.Link;
				} else {
					actualSubmissionType = submissionMode; // Fallback
				}

				// For backward compatibility: set submissionLink to first link or first file URL (optional for new format)
				if (submittedLinks.length > 0) {
					submissionPayloadLink = submittedLinks[0].url;
				} else if (submittedFiles.length > 0) {
					submissionPayloadLink = submittedFiles[0].fileUrl;
				}
			} else {
				// Legacy format - use submissionMode
				actualSubmissionType = submissionMode;
			}

			submitAssignmentMutation.mutate({
				applicationId: application._id || '',
				submissionLink: submissionPayloadLink || undefined, // Don't send empty string for new format
				submittedLinks: submittedLinks.length > 0 ? submittedLinks : undefined,
				submittedFiles: submittedFiles.length > 0 ? submittedFiles : undefined,
				userId: application.user?._id,
				submissionType: actualSubmissionType,
			});
		} catch (error: any) {
			toast({
				title: error?.message || 'Unable to submit assignment',
				type: 'error',
			});
		} finally {
			setIsUploadingSubmission(false);
		}
	};
	const requiresFileSubmission = submissionMode === AssignmentSubmissionType.File;
	const hasSubmissionInput = requiresFileSubmission ? !!submissionFile : !!submissionLink;
	const submissionDeadlinePassed = Boolean(shouldDisableSubmission());
	const isSubmitButtonDisabled =
		submitAssignmentMutation.isLoading || isUploadingSubmission || submissionDeadlinePassed || !hasSubmissionInput;
	const expiryDateText = ongoingRound?.expiry ? moment(ongoingRound?.expiry).format('DD MMM, hh:mm A') : '';

	const jobSeekerExpiryDateText = jobseekerExpiryDate ? moment(jobseekerExpiryDate).format('DD MMM, hh:mm A') : '';

	const finalExpiryDateText = isJobseekerLoggedIn ? jobSeekerExpiryDateText : expiryDateText;

	const [showSubmitUi, setShowSubmitUi] = useState<boolean>(true);

	useEffect(() => {
		if (submissionLink) {
			setShowSubmitUi(false);
		}
	}, []);
	const { themeMode } = useThemeContext();
	const textInputStyle =
		themeMode === 'dark'
			? { color: 'white', backgroundColor: '#4B5563', border: '1px solid #6B7280' }
			: { color: 'black', backgroundColor: 'white', border: '1px solid #D1D5DB' };

	const rejectActionMutation = useMutation(['rejectAction'], rejectAction, {
		onSuccess() {
			toast({
				title: 'Action successfull!',
				type: 'success',
			});
			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error while performing this action: ${error.message}`,
				type: 'error',
			});
		},
	});

	const approveActionMutation = useMutation(['approveAction'], approveAction, {
		onSuccess() {
			toast({
				title: 'Action successfull!',
				type: 'success',
			});

			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error while performing this action: ${error.message}`,
				type: 'error',
			});
		},
	});

	const currentRound = application?.ongoingRound?.order || 0;
	const onComplete = () => {
		setIsLoading(false);
		setRemarksDrawer(false);
		setRemarks('');
		setOpenMoveToNextRoundDrawer(false);
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete('viewAssignment');
		setSearchParams(newSearchParams);
	};
	const moveUserToNextRoundFn = useMutation(['moveToNextRound'], moveToNextRound, {
		onSuccess(data) {
			toast({
				title: data.nextRoundExists
					? `Applicant moved to ${currentRound ?? 0 + 1}`
					: `Applicant successfully qualified all rounds`,
				type: 'success',
			});
			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const rejectCandidateFn = useMutation(['rejectCandidate'], rejectCandidate, {
		onSuccess() {
			toast({
				title: `Action Successfull!`,
				type: 'success',
			});
			setIsLoading(false);
			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});
	const [remarksDrawer, setRemarksDrawer] = useState(false);
	const [openMoveToNextRoundDrawer, setOpenMoveToNextRoundDrawer] = useState(false);

	const [remarksDrawerType, setRemarksDrawerType] = useState<UIRemarksDrawerType>(
		UIRemarksDrawerType.REJECT_ASSIGNMENT,
	);
	const [remarksDrawerCtaText, setRemarksDrawerCtaText] = useState<string>('Submit');
	const [remarks, setRemarks] = useState<string>('');
	const lastChatThread = ongoingRound?.approvalChatThread?.[ongoingRound?.approvalChatThread?.length - 1]?.data || '';
	const handleSubmit = () => {
		setIsLoading(true);
		switch (remarksDrawerType) {
			case UIRemarksDrawerType.REJECT_ASSIGNMENT:
				rejectCandidateFn.mutate({
					userId: typeof application.user === 'string' ? application.user : application.user?._id,
					positionId: positionId || '',
					remarks,
				});
				break;
			case UIRemarksDrawerType.REQUEST_CORRECTION_IN_ASSIGNMENT:
				rejectActionMutation.mutate({
					applicationId: application._id || '',
					positionId: positionId || '',
					approvalType: 'assignment',
					remarks,
				});
				break;
		}
	};

	return (
		<>
			{!isJobseekerLoggedIn && (
				<div className="py-[24px] text-lg font-inter flex font-medium mt-[16px] items-center">
					<span
						className="flex items-center cursor-pointer"
						onClick={() => {
							// navigate to previous page
							searchParams.delete('viewAssignment');
							setSearchParams(searchParams);
							refetchPositionById();
						}}
					>
						<span className="mr-3 inline-block align-bottom">
							<Img loading="lazy" src="/assets/arrowLeft.svg" alt="arrowLeft" width="24" height="24" />
						</span>
						All Candidates
					</span>
				</div>
			)}
			<div className="max-w-[720px] flex flex-col ">
				<p className="font-inter text-[18px] font-[500] leading-[28px]">{ongoingRound?.title}</p>
				{finalExpiryDateText && (
					<div className="flex gap-[4px] mt-[6px]">
						<p className="dark:text-[#C7C9D9] italic font-inter text-[16px] font-[400] leading-[24px]">
							{user?.flags?.isMasaiStudent ? 'Suggested deadline' : 'Expires at'}
						</p>
						<div className="flex items-center gap-[4px]">
							<HiClock className="w-[20px] h-[20px] dark:fill-[#FDED72] fill-[#E57A00]" />
							<p className="dark:text-[#FDED72] text-[#E57A00] font-inter text-[16px] font-[500] leading-[24px]">
								{finalExpiryDateText}
							</p>
						</div>
					</div>
				)}
				{/* {user?.flags?.isMasaiStudent && (
					<div className="mt-[6px]">
						<p className="dark:text-[#C7C9D9] italic font-inter text-[14px] font-[400] leading-[20px]">
							Note: As a Masai student, you can submit your assignment anytime - there's no strict deadline.
						</p>
					</div>
				)} */}
				{isAccountManagerLoggedIn && jobSeekerExpiryDateText && (
					<div className="flex gap-[4px] mt-[6px]">
						<p className="dark:text-[#C7C9D9] italic font-inter text-[16px] font-[400] leading-[24px]">
							Deadline for Jobseeker Submissions:{' '}
						</p>
						<div className="flex items-center gap-[4px]">
							<img
								src={themeMode === 'dark' ? '/assets/yellow-clock.svg' : '/assets/orange-clock.svg'}
								className="w-[20px] h-[20px]"
								alt={jobSeekerExpiryDateText}
							/>
							<p className="dark:text-[#FDED72] text-[#E57A00] font-inter text-[16px] font-[500] leading-[24px]">
								{jobSeekerExpiryDateText}
							</p>
						</div>
					</div>
				)}
				<p className="mb-[16px] mt-[20px] font-inter text-[16px] font-[400] leading-[24px] dark:text-[#C7C9D9] break-words">
					{ongoingRound?.description}
				</p>
				{assignmentExternalLink && !isOtherSubmission && (
					<a
						target="_blank"
						rel="noreferrer"
						href={assignmentExternalLink}
						className="dark:bg-[#18222D] bg-[gray] text-[#fff] w-fit px-[10px] py-[6px] rounded-[24px] font-inter text-[14px] font-[400] leading-[20px] mb-2 inline-flex items-center gap-2"
					>
						{isOnlineTest
							? 'Take Online Test'
							: assignmentExternalLink.split('/').pop() || 'Open assignment link'}
					</a>
				)}
				{assignmentDocumentLink && (
					<a
						target="_blank"
						rel="noreferrer"
						href={assignmentDocumentLink}
						className="dark:bg-[#18222D] bg-[gray] text-[#fff] w-fit px-[10px] py-[6px] rounded-[24px] font-inter text-[14px] font-[400] leading-[20px] inline-flex items-center gap-2"
					>
						Download assignment file
					</a>
				)}
				{(assignmentLinkOptions.length > 0 || assignmentFileOptions.length > 0) && (
					<div className="mt-[16px] border border-dashed border-gray-500 rounded-[8px] p-[12px] text-sm">
						<p className="font-semibold mb-2">Allowed submission formats</p>
						{assignmentLinkOptions.length > 0 && (
							<p className="mb-1 text-black">
								Links:{' '}
								{isOtherSubmission && assignmentExternalLink ? (
									<a
										href={assignmentExternalLink}
										target="_blank"
										rel="noreferrer"
										className="text-[#7777E7] underline"
									>
										{assignmentExternalLink}
									</a>
								) : (
									assignmentLinkOptions
										.map(option => linkOptionLabelMap[option] || option)
										.join(', ')
								)}
							</p>
						)}
						{assignmentFileOptions.length > 0 && (
							<p className="text-black">
								Files:{' '}
								{assignmentFileOptions
									.map(option => fileOptionLabelMap[option] || option)
									.join(', ')}
							</p>
						)}
					</div>
				)}
				{/* Instructions removed here - shown in the "Other" submission section below to avoid duplication */}
				{lastChatThread && user?.role && [UserRole.JOBSEEKER, UserRole.EXPERIENCED_JOBSEEKER].includes(user?.role) && (
					<div className="border border-[#C7C9D933] bg-[#18222D] mt-[14px] rounded-[4px] p-[10px]">
						<p className="text-[#F2F2F5]">
							<span className="text-[#FF3B3B]">Comments: </span>
							{lastChatThread}
						</p>
					</div>
				)}
				{(user?.role === UserRole.RECRUITER ||
					user?.role === UserRole.ACCOUNT_MANAGER ||
					user?.role === UserRole.SUPER_ACCOUNT_MANAGER ||
					user?.role === UserRole.ADMIN ||
					user?.role === UserRole.SUPER_ADMIN) &&
					((lastSubmission?.submittedLinks && lastSubmission.submittedLinks.length > 0) ||
						(lastSubmission?.submittedFiles && lastSubmission.submittedFiles.length > 0) ||
						submissionLink) && (
						<div className="border border-[#E4E4EB] mt-[32px] py-[12px] px-[16px] rounded-[8px] bg-[#E5F0FF] space-y-4">
							{lastSubmission?.submittedLinks && lastSubmission.submittedLinks.length > 0 ? (
								<div>
									<p className="font-inter text-[14px] font-[500] leading-[21px]">Submitted Links:</p>
									<div className="mt-[8px] space-y-1">
										{lastSubmission.submittedLinks.map((link, index) => (
											<div key={index}>
												<p className="text-[12px] font-semibold text-gray-600">{link.type}</p>
												<a
													href={link.url}
													target="_blank"
													rel="noreferrer"
													className="text-[#7777E7] break-all"
												>
													{link.url}
												</a>
											</div>
										))}
									</div>
								</div>
							) : null}
							{lastSubmission?.submittedFiles && lastSubmission.submittedFiles.length > 0 ? (
								<div>
									<p className="font-inter text-[14px] font-[500] leading-[21px]">Submitted Files:</p>
									<div className="mt-[8px] space-y-1">
										{lastSubmission.submittedFiles.map((file, index) => (
											<div key={index}>
												<p className="text-[12px] font-semibold text-gray-600">{file.type}</p>
												<a
													href={file.fileUrl}
													target="_blank"
													rel="noreferrer"
													className="text-[#7777E7] break-all"
												>
													{file.fileName} {file.fileSize ? `(${(file.fileSize / 1024).toFixed(2)} KB)` : ''}
												</a>
											</div>
										))}
									</div>
								</div>
							) : null}
							{!lastSubmission?.submittedLinks?.length && !lastSubmission?.submittedFiles?.length && submissionLink && (
								<div>
									<p className="font-inter text-[14px] font-[500] leading-[21px]">
										{isOnlineTest ? 'Online test link' : 'Github assignment link'}
									</p>
									<a
										target="_blank"
										rel="noreferrer"
										href={submissionLink}
										className=" mt-[4px] text-[#7777E7] flex items-center max-w-full truncate"
									>
										{submissionLink}
									</a>
								</div>
							)}
						</div>
					)}
				{/* // */}
				{isJobseekerLoggedIn ? (
					!user?.flags?.isMasaiStudent && isJobSeekerDeadlinePassed ? (
						<p className="mt-[24px] text-[#FDDD48] italic font-inter text-[16px] font-[400] leading-[24px]">
							No longer accepting submissions as the deadline has passed.
						</p>
					) : (
						<>
							{/* Special UI for "No Submission" type - manual checkbox acknowledgment */}
							{isNoSubmission && assignmentExternalLink ? (
								<div className="mt-[32px]">
									{/* Show existing submission if already submitted */}
									{lastSubmission && (
										<div className="border border-[#E4E4EB] py-[12px] px-[16px] rounded-[8px] bg-[#E5F0FF] dark:bg-[#1a2332] mb-4">
											<p className="font-inter text-[14px] font-[500] leading-[21px] mb-2">Submission Status:</p>
											<p className="text-[14px] text-gray-700 dark:text-gray-300">
												You have acknowledged that you have completed/submitted this assignment.
											</p>
											{lastSubmission?.submittedLinks?.find(l => l.type === 'noSubmission') && (
												<p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1">
													Assignment acknowledged
												</p>
											)}
										</div>
									)}

									{/* Instructions and Checkbox for No Submission */}
									{!lastSubmission && (
										<>
											<div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-[8px] p-[16px]">
												<p className="font-semibold text-[14px] mb-2 text-blue-900 dark:text-blue-200">Instructions:</p>
												<p className="text-[14px] text-blue-800 dark:text-blue-300">
													No submission is required for this assignment. Please review the assignment link above, and then check the box below to acknowledge that you have completed/submitted the assignment.
												</p>
											</div>

											{/* Acknowledgment Checkbox */}
											<div className="mb-4">
												<label className="flex items-start gap-2 cursor-pointer">
													<input
														type="checkbox"
														checked={noSubmissionAcknowledgmentChecked}
														onChange={e => setNoSubmissionAcknowledgmentChecked(e.target.checked)}
														disabled={submissionDeadlinePassed}
														className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
													/>
													<span className="text-[14px] dark:text-[#FFF]">
														I acknowledge that I have completed/submitted the assignment
													</span>
												</label>
											</div>

											{/* Submit Button */}
											<Button
												onClick={async () => {
													if (!noSubmissionAcknowledgmentChecked) {
														toast({
															title: 'Please acknowledge that you have completed/submitted the assignment',
															type: 'error',
														});
														return;
													}
													try {
														setIsUploadingSubmission(true);
														// Submit acknowledgment that user has completed the assignment
														submitAssignmentMutation.mutate({
															applicationId: application._id || '',
															submissionLink: assignmentExternalLink,
															submittedLinks: [{ type: 'noSubmission', url: assignmentExternalLink }],
															userId: application.user?._id,
															submissionType: AssignmentSubmissionType.Link,
														});
													} catch (error: any) {
														toast({
															title: error?.message || 'Unable to submit acknowledgment',
															type: 'error',
														});
													} finally {
														setIsUploadingSubmission(false);
													}
												}}
												disabled={submitAssignmentMutation.isLoading || isUploadingSubmission || submissionDeadlinePassed || !noSubmissionAcknowledgmentChecked}
												size={'lg'}
												color="purplePlaced"
												className="w-[100%] md:max-w-[280px]"
											>
												{submitAssignmentMutation.isLoading || isUploadingSubmission ? <Spinner /> : 'Submit Acknowledgment'}
											</Button>
										</>
									)}
								</div>
							) : isOtherSubmission && assignmentExternalLink ? (
								<div className="mt-[32px]">
									{/* Show existing submission if already submitted */}
									{lastSubmission && !submissionDeadlinePassed && (
										<div className="border border-[#E4E4EB] py-[12px] px-[16px] rounded-[8px] bg-[#E5F0FF] dark:bg-[#1a2332] mb-4">
											<p className="font-inter text-[14px] font-[500] leading-[21px] mb-2">Submission Status:</p>
											<p className="text-[14px] text-gray-700 dark:text-gray-300">
												You have already submitted this assignment.
											</p>
										</div>
									)}

									{/* Instructions */}
									{ongoingRound?.assignmentSubmissionNotes && (
										<div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-[8px] p-[16px]">
											<p className="font-semibold text-[14px] mb-2 text-blue-900 dark:text-blue-200">Instructions:</p>
											<p className="text-[14px] text-blue-800 dark:text-blue-300">{ongoingRound.assignmentSubmissionNotes}</p>
										</div>
									)}

									{/* Submission URL */}
									<div className="mb-4">
										<p className="font-inter text-[14px] font-[500] leading-[21px] mb-2 dark:text-[#FFF]">
											Submission Form:
										</p>
										<a
											href={assignmentExternalLink}
											target="_blank"
											rel="noreferrer"
											className="text-[#7777E7] underline text-[14px] break-all"
										>
											{assignmentExternalLink}
										</a>
									</div>

									{/* Acknowledgment Checkbox - only show if not already submitted */}
									{!lastSubmission && (
										<>
											<div className="mb-4">
												<label className="flex items-start gap-2 cursor-pointer">
													<input
														type="checkbox"
														checked={acknowledgmentChecked}
														onChange={e => setAcknowledgmentChecked(e.target.checked)}
														disabled={submissionDeadlinePassed}
														className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
													/>
													<span className="text-[14px] dark:text-[#FFF]">
														I acknowledge that I have filled out the submission form at the link above.
													</span>
												</label>
											</div>

											{/* Submit Button */}
											<Button
												onClick={handleAssignmentSubmission}
												disabled={submitAssignmentMutation.isLoading || isUploadingSubmission || submissionDeadlinePassed || !acknowledgmentChecked}
												size={'lg'}
												color="purplePlaced"
												className="w-[100%] md:max-w-[280px]"
											>
												{submitAssignmentMutation.isLoading || isUploadingSubmission ? <Spinner /> : 'Submit'}
											</Button>
										</>
									)}
								</div>
							) : (hasLinkOptions || hasFileOptions) && !isNoSubmission ? (
								<div className="mt-[32px]">
									{/* Show existing structured submissions */}
									{hasExistingStructuredSubmission && !showStructuredSubmitUi && !submissionDeadlinePassed && (
										<div className="border border-[#E4E4EB] py-[12px] px-[16px] rounded-[8px] bg-[#E5F0FF] dark:bg-[#1a2332] space-y-4 mb-4">
											{lastSubmission?.submittedLinks && lastSubmission.submittedLinks.length > 0 && (
												<div>
													<p className="font-inter text-[14px] font-[500] leading-[21px] mb-2">Submitted Links:</p>
													<div className="space-y-2">
														{lastSubmission.submittedLinks.map((link, index) => (
															<div key={index} className="flex items-center gap-2">
																<span className="text-[12px] text-gray-600 dark:text-gray-400 capitalize min-w-[100px]">{linkOptionLabelMap[link.type] || link.type}:</span>
																<a
																	target="_blank"
																	rel="noreferrer"
																	href={link.url}
																	className="text-[#7777E7] text-[14px] truncate flex-1"
																>
																	{link.url}
																</a>
															</div>
														))}
													</div>
												</div>
											)}
											{lastSubmission?.submittedFiles && lastSubmission.submittedFiles.length > 0 && (
												<div>
													<p className="font-inter text-[14px] font-[500] leading-[21px] mb-2">Submitted Files:</p>
													<div className="space-y-2">
														{lastSubmission.submittedFiles.map((file, index) => (
															<div key={index} className="flex items-center gap-2">
																<span className="text-[12px] text-gray-600 dark:text-gray-400 capitalize min-w-[100px]">{fileOptionLabelMap[file.type] || file.type}:</span>
																<a
																	target="_blank"
																	rel="noreferrer"
																	href={file.fileUrl}
																	className="text-[#7777E7] text-[14px] truncate flex-1"
																>
																	{file.fileName}
																</a>
															</div>
														))}
													</div>
												</div>
											)}
											<button
												onClick={() => setShowStructuredSubmitUi(true)}
												className="text-gray-800 dark:!text-[#CDF] text-[14px] font-[500] mt-2"
											>
												Edit
											</button>
										</div>
									)}

									{/* Edit/Submit Form - only show if editing or no submission exists */}
									{(!hasExistingStructuredSubmission || showStructuredSubmitUi) && (
										<>
											<div
												className={`space-y-6 ${hasLinkOptions && hasFileOptions ? 'md:grid md:grid-cols-2 md:gap-6 md:space-y-0' : ''
													}`}
											>
												{/* Links Section */}
												{hasLinkOptions && (
													<div className="space-y-4">
														<h3 className="font-semibold text-[16px] mb-4">Required Links</h3>
														{assignmentLinkOptions.map(opt => (
															<div key={opt}>
																<Label className="dark:text-[#FFF] font-inter text-[14px] font-[500] leading-[21px] mb-2">
																	{linkOptionLabelMap[opt] || opt} <span className="text-red-600">*</span>
																</Label>
																<TextInput
																	disabled={submissionDeadlinePassed}
																	type="url"
																	placeholder={`Enter ${linkOptionLabelMap[opt]} URL`}
																	value={linkSubmissions[opt] || ''}
																	onChange={e => setLinkSubmissions(prev => ({ ...prev, [opt]: e.target.value }))}
																	style={textInputStyle}
																/>
															</div>
														))}
													</div>
												)}

												{/* Files Section */}
												{hasFileOptions && (
													<div className="space-y-4">
														<h3 className="font-semibold text-[16px] mb-4">Required Files</h3>
														{assignmentFileOptions.map(opt => {
															// Check if there's a new file uploaded OR an existing file from previous submission
															const newFile = fileSubmissions[opt];
															const existingFile = lastSubmission?.submittedFiles?.find(f => f.type === opt);
															const isReplacing = filesToReplace.has(opt);
															const shouldShowPreview = (newFile || existingFile) && !isReplacing;

															return (
																<div key={opt}>
																	<Label className="dark:text-[#FFF] font-inter text-[14px] font-[500] leading-[21px] mb-2">
																		{fileOptionLabelMap[opt] || opt} <span className="text-red-600">*</span>
																	</Label>
																	{shouldShowPreview ? (
																		<div className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
																			<div className="flex-1">
																				<p className="text-sm font-medium dark:text-white">
																					{newFile ? newFile.name : existingFile?.fileName}
																					{!newFile && existingFile && (
																						<span className="ml-2 text-xs text-green-600 dark:text-green-400">(Previously uploaded)</span>
																					)}
																				</p>
																				<p className="text-xs text-gray-500 dark:text-gray-400">
																					{newFile
																						? `${(newFile.size / 1024).toFixed(2)} KB`
																						: existingFile?.fileSize
																							? `${(existingFile.fileSize / 1024).toFixed(2)} KB`
																							: ''
																					}
																				</p>
																			</div>
																			<button
																				type="button"
																				onClick={() => {
																					if (newFile) {
																						// Remove new file
																						setFileSubmissions(prev => {
																							const updated = { ...prev };
																							delete updated[opt];
																							return updated;
																						});
																					} else if (existingFile) {
																						// Mark for replacement
																						setFilesToReplace(prev => new Set(prev).add(opt));
																					}
																				}}
																				disabled={submissionDeadlinePassed}
																				className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
																			>
																				{newFile ? 'Remove' : 'Replace'}
																			</button>
																		</div>
																	) : (
																		<FileInput
																			disabled={submissionDeadlinePassed}
																			accept={opt === 'other' ? undefined : fileAcceptMap[opt]}
																			onChange={event => {
																				const file = event.target.files?.[0];
																				if (file) {
																					setFileSubmissions(prev => ({ ...prev, [opt]: file }));
																					// Remove from filesToReplace when new file is selected
																					setFilesToReplace(prev => {
																						const updated = new Set(prev);
																						updated.delete(opt);
																						return updated;
																					});
																				}
																			}}
																		/>
																	)}
																</div>
															);
														})}
													</div>
												)}
											</div>

											<Button
												onClick={handleAssignmentSubmission}
												disabled={submitAssignmentMutation.isLoading || isUploadingSubmission || submissionDeadlinePassed}
												size={'lg'}
												color="purplePlaced"
												className="mt-[24px] w-[100%] md:max-w-[280px]"
											>
												{submitAssignmentMutation.isLoading || isUploadingSubmission ? <Spinner /> : 'Submit'}
											</Button>
										</>
									)}
								</div>
							) : !isNoSubmission ? (
								/* Legacy single submission UI */
								<div className="mt-[32px]">
									<div className="mb-2 block">
										<Label
											htmlFor="title"
											value={
												submissionMode === AssignmentSubmissionType.File
													? 'Upload assignment file '
													: isOnlineTest
														? 'Online test link '
														: 'Assignment submission link '
											}
											className="dark:text-[#FFF] font-inter text-[14px] font-[500] leading-[21px]"
										/>
										<span className="text-red-600">*</span>
									</div>

									{submissionLink && !showSubmitUi && !submissionDeadlinePassed && (
										<div className="my-[16px] flex items-center gap-[8px]">
											<p className="py-[6px] px-[10px] rounded-[24px] bg-[#18222D] text-[#fff] flex items-center max-w-full truncate">
												{submissionLink}
											</p>
											<button
												onClick={() => {
													setSubmissionFile(null);
													setShowSubmitUi(true);
												}}
												className="text-gray-800 dark:!text-[#CDF] text-[12px] font-[400] leading-[16px]"
											>
												Edit
											</button>
										</div>
									)}
									{showSubmitUi && canSubmitLink && canSubmitFile ? (
										<div className="flex gap-4 mb-4">
											<label className="flex items-center gap-2 text-sm">
												<Radio
													checked={submissionMode === AssignmentSubmissionType.Link}
													onChange={() => setSubmissionMode(AssignmentSubmissionType.Link)}
												/>
												Submit link
											</label>
											<label className="flex items-center gap-2 text-sm">
												<Radio
													checked={submissionMode === AssignmentSubmissionType.File}
													onChange={() => setSubmissionMode(AssignmentSubmissionType.File)}
												/>
												Upload file
											</label>
										</div>
									) : null}
									{showSubmitUi && submissionMode === AssignmentSubmissionType.Link && canSubmitLink && (
										<TextInput
											disabled={submissionDeadlinePassed}
											type="url"
											id="title"
											name="submissionLink"
											color={'gray'}
											onChange={e => setSubmissionLink(e.target.value)}
											value={submissionLink}
											placeholder={
												isOnlineTest ? 'https://example.com/online-test' : 'https://github.com/username/project'
											}
											style={textInputStyle}
										/>
									)}
									{showSubmitUi && submissionMode === AssignmentSubmissionType.File && canSubmitFile && (
										<div>
											<FileInput
												disabled={submissionDeadlinePassed}
												accept={fileAcceptAttr}
												onChange={event => {
													const file = event.target.files?.[0];
													setSubmissionFile(file || null);
												}}
											/>
											<p className="mt-2 text-xs text-gray-300">
												Accepted formats: {assignmentFileOptions.length ? assignmentFileOptions.map(option => fileOptionLabelMap[option] || option).join(', ') : 'PDF, DOC, DOCX'}
											</p>
											{submissionFile && (
												<p className="mt-1 text-xs text-gray-400">Selected file: {submissionFile.name}</p>
											)}
										</div>
									)}
									{showSubmitUi && (
										<Button
											onClick={handleAssignmentSubmission}
											disabled={isSubmitButtonDisabled}
											size={'lg'}
											color="purplePlaced"
											className="mt-[16px] w-[100%] md:max-w-[280px]"
										>
											{submitAssignmentMutation.isLoading || isUploadingSubmission ? <Spinner /> : 'Submit'}
										</Button>
									)}
								</div>
							) : null}
						</>
					)
				) : null}
				{((user?.role === UserRole.RECRUITER && isDeadlinePassed) ||
					((user?.role === UserRole.ACCOUNT_MANAGER || user?.role === UserRole.SUPER_ACCOUNT_MANAGER) &&
						isDeadlinePassed &&
						ongoingRound?.status === ApplicationRoundStatus.Submitted)) && (
						<div className="grid md:flex gap-[8px] mt-[16px]">
							<Button
								onClick={() => {
									setIsLoading(true);
									setOpenMoveToNextRoundDrawer(true);
								}}
								className=""
								color="purplePlaced"
							>
								{moveUserToNextRoundFn.isLoading ? <Spinner /> : 'Move to next round'}
							</Button>
							<Button
								onClick={() => {
									setRemarksDrawer(true);
									setRemarksDrawerCtaText('Reject Assignment');
									setRemarksDrawerType(UIRemarksDrawerType.REJECT_ASSIGNMENT);
								}}
								color="gray"
								className="border border-[#6B7280] bg-[#4B5563] text-[#fff]"
							>
								Reject Candidate
							</Button>
						</div>
					)}
				{user?.role === UserRole.RECRUITER && !isDeadlinePassed && (
					<div className="mt-[16px]">
						{/* notify recruiter that the submisson will be visible to them after the deadline passes */}
						<p>
							<span className="text-[orange] italic font-inter text-[16px] font-[400] leading-[24px]">
								Note: The submission will be visible after the deadline passes.
							</span>
						</p>
					</div>
				)}
				{(user?.role === UserRole.ACCOUNT_MANAGER || user?.role === UserRole.SUPER_ACCOUNT_MANAGER) &&
					ongoingRound?.status === ApplicationRoundStatus.PendingForSubmissionApproval && (
						<div className="grid md:flex gap-[8px] mt-[16px]">
							<Button
								onClick={() => {
									setIsLoading(true);
									approveActionMutation.mutate({
										applicationId: application._id || '',
										positionId: positionId || '',
										approvalType: 'assignment',
										remarks: '',
									});
								}}
								className=""
								color="purplePlaced"
							>
								{approveActionMutation.isLoading ? <Spinner /> : 'Approve Assignment'}
							</Button>
							<Button
								onClick={() => {
									setRemarksDrawer(true);
									setRemarksDrawerCtaText('Request Correction');
									setRemarksDrawerType(UIRemarksDrawerType.REQUEST_CORRECTION_IN_ASSIGNMENT);
								}}
								disabled={isLoading}
								color="light"
							>
								Request Correction
							</Button>
						</div>
					)}
			</div >

			<Drawer
				minHeight="max-content"
				maxHeight="90dvh"
				isModal={!isMobile}
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={openMoveToNextRoundDrawer}
				onClose={() => {
					setOpenMoveToNextRoundDrawer(false);
				}}
				header={
					<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">
						Move To Next Round
					</h1>
				}
				body={
					<div className="px-3 py-4">
						{
							<RecruiterCandidateMoveToNextRound
								positionId={positionId ?? ''}
								userId={applicationUser?._id}
								userName={application?.user?.name}
								isLoading={moveUserToNextRoundFn?.isLoading}
								currentRound={application?.ongoingRound?.order || 0}
								totalRounds={application?.applicationRounds?.length || 0}
							/>
						}
					</div>
				}
			/>

			<Drawer
				minHeight="max-content"
				maxHeight="90dvh"
				isModal={!isMobile}
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={remarksDrawer}
				onClose={() => {
					setRemarksDrawer(false);
				}}
				header={
					<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">Add Remarks</h1>
				}
				body={
					<div className="px-3 py-4">
						{
							<RemarksDrawer
								remarks={remarks}
								setRemarks={setRemarks}
								isLoading={
									isLoading ||
									rejectActionMutation.isLoading ||
									moveUserToNextRoundFn.isLoading ||
									rejectCandidateFn.isLoading
								}
								ctaText={remarksDrawerCtaText}
								type=""
								handleSubmit={handleSubmit}
								remarksDrawerType={remarksDrawerType}
							/>
						}
					</div>
				}
			/>
		</>
	);
};

export default ViewAssignment;
